<template>
  <div class="no-select video-container">
    <div
      v-if="isActive"
      ref="programVideoPlayer"
    >
      <div
        v-if="!player"
        class="video-js vjs-16-9 relative-position"
      >
        <span class="absolute-center">
          <q-spinner size="lg" />
        </span>
      </div>
      <div
        v-else-if="$slots['top-right']"
        class="relative-position"
      >
        <div
          class="absolute-top-right q-pa-md"
          style="z-index: 10;"
        >
          <slot name="top-right" />
        </div>
      </div>
    </div>
    <q-card
      v-else-if="showIfInactive"
      class="q-ma-xs q-py-md row text-center justify-center items-center"
    >
      <q-card-section class="items-center">
        <q-spinner
          v-if="isProcessing"
          size="lg"
          color="grey-6"
        />
        <div class="text-subtitle2 text-center q-ma-sm">
          {{ statusText }}
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import 'video.js/dist/video-js.css';

import { storage } from 'boot/firebase';
import { getDownloadURL, ref } from 'firebase/storage';
import { get } from 'lodash';
import { mapActions } from 'pinia';
import { sessions } from 'src/store/sessions';
import videojs from 'video.js';

const PREVIEW_CHECK_TIMEOUT_MS = 3000;

export default {
  name: 'ProgramVideo',

  props: {
    videoInfo: {
      type: Object,
      required: true,
    },
    noControls: {
      type: Boolean,
      default: false,
    },
    showIfInactive: {
      type: Boolean,
      default: false,
    },
    maxSeek: {
      type: Number,
      default: null,
    },
    timeUpdateInterval: {
      type: Number,
      default: 1,
    },
    sessionPaused: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['video-completed', 'time-update', 'click'],

  data() {
    return {
      player: null,
      videoSrc: null,
      isPreviewAvailable: null,

      nextUpdateTime: 0,
    };
  },

  computed: {

    coverPhoto() {
      return get(this.videoInfo, 'coverphoto', null);
    }, // coverPhoto()

    status() {
      return get(this.videoInfo, 'status', 'active');
    }, // status()

    isActive() {
      return this.status === 'active' || (this.status === 'selected' && this.isPreviewAvailable !== false);
    }, // isActive()

    sourceType() {
      const { type } = this.videoInfo;

      // canPlayVideo() already confirmed the file plays; label it video/mp4 so video.js
      // doesn't reject a type the browser doesn't recognize (e.g. video/quicktime, or empty)
      if (this.status === 'selected' && !(type && document.createElement('video').canPlayType(type))) {
        return 'video/mp4';
      }
      return type;
    }, // sourceType()

    isProcessing() {
      return ['uploading', 'transcoding'].includes(this.status);
    }, // isProcessing()

    statusText() {
      switch (this.status) {
        case 'selected':
          return this.$t('programs.programVideo.previewUnavailable');
        case 'uploading':
          return this.$t('programs.programVideo.uploadInProgress');
        case 'transcoding':
          return this.$t('programs.programVideo.videoProcessingInProgress');
        case 'failed':
          return this.$t('programs.programVideo.textProcessingError');
        case 'cancelled':
          return this.$t('programs.programVideo.textCancelledError');
        default:
          return this.$t('programs.programVideo.textUnknownFileStatus');
      }
    }, // statusText()

  },

  watch: {
    videoInfo: {
      deep: true,
      immediate: true,
      async handler(newVideoInfo, oldVideoInfo) {
        const src = get(newVideoInfo, 'src');
        if (src && src !== get(oldVideoInfo, 'src')) {
          if (newVideoInfo.status === 'selected') {
            this.isPreviewAvailable = null;
            const isPlayable = await this.canPlayVideo(src);
            if (this.videoInfo?.src !== src) {
              return;
            }
            if (!isPlayable) {
              this.disposeVideoPlayer();
              this.player = null;
            }
            this.isPreviewAvailable = isPlayable;
          }
          this.$nextTick(() => {
            // Make sure video DIV is displayed if status has changed
            this.loadVideo(src);
          });
        }
        if (newVideoInfo.originalFilename && newVideoInfo.status === 'active') {
          const videoSrc = await this.accessFile(newVideoInfo.originalFilename);
          this.$nextTick(() => {
            // Make sure video DIV is displayed if status has changed
            this.loadVideo(videoSrc);
          });
        }
      },
    },
    sessionPaused(isPaused) {
      if (!this.player) {
        return;
      }

      if (isPaused) {
        this.player.pause();
      }
    },
  },

  beforeUnmount() {
    this.disposeVideoPlayer();
  },

  methods: {
    ...mapActions(sessions, ['setIsTrainingVideoPlaying']),

    disposeVideoPlayer() {
      if (this.player) {
        this.player.dispose();
      }
    },

    seekingEvent() {
      if (!this.player || this.maxSeek === null) {
        return;
      }
      if (this.player.currentTime() > this.maxSeek) {
        this.player.currentTime(this.maxSeek);
      }
    },

    endedEvent() {
      this.$emit('video-completed');
    },

    timeUpdateEvent() {
      if (!this.player || this.player.seeking()) {
        return;
      }
      const currentTime = this.player.currentTime();
      if (currentTime >= this.nextUpdateTime) {
        this.nextUpdateTime = currentTime + this.timeUpdateInterval;
        this.$emit('time-update', currentTime);
      }
    },

    loadVideo(videoSrc) {
      if (!this.isActive || !this.$refs.programVideoPlayer) {
        return;
      }

      if (this.player) {
        this.disposeVideoPlayer();
      }

      const videoEl = videojs.dom.createEl('video', {
        className: 'video-js vjs-16-9',
      });

      this.$refs.programVideoPlayer.appendChild(videoEl);

      const videoPlayerOptions = {
        autoPlay: false,
        controls: !this.noControls,
        playsinline: true,
        sources: [
          {
            src: videoSrc,
            ...(this.sourceType ? { type: this.sourceType } : {}),
          },
        ],
      };

      videojs.addLanguage(
        'en',
        {
          // eslint-disable-next-line max-len
          'The media could not be loaded, either because the server or network failed or because the format is not supported.': 'Preview not available...',
          // eslint-disable-next-line max-len
          'No compatible source was found for this media.': 'Video preview not available. The video will be uploaded when you save the program.',
        },
      );

      this.player = videojs(videoEl, videoPlayerOptions, () => {
        this.player.poster(this.coverPhoto);
        this.player.off(['click', 'tap']);
        this.player.on(['click', 'tap'], (event) => {
          event.preventDefault();
          // do the action
          this.$emit('click');
        });
        this.player.on('seeking', this.seekingEvent);
        this.player.on('play', () => {
          this.setIsTrainingVideoPlaying(true);
        });

        this.player.on('pause', () => {
          this.setIsTrainingVideoPlaying(false);
        });

        this.player.on('ended', () => {
          this.endedEvent();
          this.setIsTrainingVideoPlaying(false);
        });
        this.player.on('timeupdate', this.timeUpdateEvent);
      });
    }, // loadVideo()

    canPlayVideo(src) {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        let timeoutId = null;
        const settle = (result) => {
          clearTimeout(timeoutId);
          video.removeAttribute('src');
          video.load();
          resolve(result);
        };

        timeoutId = setTimeout(() => settle(false), PREVIEW_CHECK_TIMEOUT_MS);
        video.onloadedmetadata = () => settle(video.videoWidth > 0);
        video.onerror = () => settle(false);
        video.preload = 'metadata';
        video.src = src;
      });
    }, // canPlayVideo()

    async accessFile(filename = null) {
      const fileRef = ref(storage, `programs/${this.videoInfo.program}/${filename}`);
      return getDownloadURL(fileRef);
    }, // accessFile()
  },
};
</script>
<style lang="scss" scoped>
.video-container {
  width: 800px;
  max-width: 100%;
  margin: 0 auto;
}
</style>
